/**
 * 侧边栏导航组件
 * 包含 Logo、新建任务按钮、导航菜单、费用统计、用户头像
 * 手机端：Ant Design Drawer
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Menu, Drawer } from 'antd';
import {
  PlusOutlined,
  HistoryOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import UserAuth from '../components/UserAuth';
import useIsMobile from '../hooks/useIsMobile';
import './Sidebar.css';

const USD_TO_RMB = 7.1;

const Sidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isTester } = useUser();
  const isMobile = useIsMobile();

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/history') return 'history';
    if (path === '/templates') return 'templates';
    if (path === '/' || path === '/new') return 'new';
    return 'new';
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile && onClose) onClose();
  };

  const menuItems = [
    {
      key: 'history',
      icon: <HistoryOutlined />,
      label: '历史任务',
      onClick: () => handleNavigate('/history'),
    },
    {
      key: 'templates',
      icon: <BookOutlined />,
      label: 'Prompt 模板库',
      onClick: () => handleNavigate('/templates'),
    },
  ];

  const getCostDisplay = () => {
    if (!user) return null;
    if (isTester()) {
      return ((user.virtual_spend || 0) * USD_TO_RMB).toFixed(4);
    }
    return ((user.total_spend || 0) * USD_TO_RMB).toFixed(4);
  };

  const sidebarContent = (
    <div className={isMobile ? 'sidebar-inner' : 'sidebar'}>
      {/* Logo */}
      <div className="sidebar-logo">
        <h1>K-label</h1>
        <div className="logo-subtitle">AI 数据打标平台</div>
      </div>

      {/* New Task Button */}
      <div className="sidebar-new-task">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            if (location.pathname === '/new') {
              navigate('/new', { state: { reset: Date.now() } });
            } else {
              navigate('/new');
            }
            if (isMobile && onClose) onClose();
          }}
        >
          新建打标任务
        </Button>
      </div>

      {/* Navigation Menu */}
      <div className="sidebar-nav">
        <Menu
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
        />
      </div>

      {/* Cost Stats */}
      {user && (
        <div className="sidebar-stats">
          <div className="sidebar-stats-title">消耗统计</div>
          <div className="sidebar-stats-item">
            <span className="sidebar-stats-label">
              {isTester() ? '金额消耗' : '总消费'}
            </span>
            <span className="sidebar-stats-value">
              ¥{getCostDisplay()}
            </span>
          </div>
        </div>
      )}

      {/* User Auth */}
      <div className="sidebar-user">
        <UserAuth />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        width={260}
        open={open}
        onClose={onClose}
        styles={{ body: { padding: 0 } }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return sidebarContent;
};

export default Sidebar;
